import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from './firebase';
import { useVerifiedDancer } from './hooks/useVerifiedDancer';
import MarketplaceItemForm from './MarketplaceItemForm';
import MarketplaceItemDetail from './MarketplaceItemDetail';
import './Marketplace.css';

// Item categories for dance marketplace
const CATEGORIES = [
  { id: 'all', label: 'All Items', icon: '✨' },
  { id: 'dresses', label: 'Dresses', icon: '👗' },
  { id: 'shoes', label: 'Shoes', icon: '👠' },
  { id: 'costumes', label: 'Costumes', icon: '🎭' },
  { id: 'accessories', label: 'Accessories', icon: '💎' },
  { id: 'practice-wear', label: 'Practice Wear', icon: '🩱' },
  { id: 'jewelry', label: 'Jewelry', icon: '📿' },
  { id: 'other', label: 'Other', icon: '📦' }
];

const LISTING_TYPES = [
  { id: 'all', label: 'All' },
  { id: 'sale', label: 'For Sale' },
  { id: 'rent', label: 'For Rent' }
];

export default function Marketplace() {
  const { user, loading: authLoading, isVerified, isAdmin, verifiedDancerName, canListItems } = useVerifiedDancer();
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [listingTypeFilter, setListingTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [myListingsView, setMyListingsView] = useState(false);
  const [myListingsFilter, setMyListingsFilter] = useState('all');
  
  // Fetch marketplace items
  useEffect(() => {
    fetchItems();
  }, [selectedCategory, listingTypeFilter, sortBy, myListingsView, myListingsFilter, user]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let q;
      const itemsRef = collection(db, 'marketplaceItems');
      
      if (myListingsView && user) {
        // Fetch user's own listings (include all statuses)
        const constraints = [where('sellerId', '==', user.uid)];
        
        if (myListingsFilter !== 'all') {
          constraints.push(where('status', '==', myListingsFilter));
        }
        
        constraints.push(orderBy('createdAt', 'desc'));
        q = query(itemsRef, ...constraints);
      } else {
        // Public view - only show approved items
        const constraints = [where('status', '==', 'approved')];
        
        if (selectedCategory !== 'all') {
          constraints.push(where('category', '==', selectedCategory));
        }
        
        if (listingTypeFilter !== 'all') {
          constraints.push(where('listingType', '==', listingTypeFilter));
        }
        
        // Add sorting
        switch (sortBy) {
          case 'newest':
            constraints.push(orderBy('approvedAt', 'desc'));
            break;
          case 'price-low':
            constraints.push(orderBy('salePrice', 'asc'));
            break;
          case 'price-high':
            constraints.push(orderBy('salePrice', 'desc'));
            break;
          default:
            constraints.push(orderBy('approvedAt', 'desc'));
        }
        
        q = query(itemsRef, ...constraints, limit(50));
      }
      
      const snapshot = await getDocs(q);
      let fetchedItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Client-side search filtering
      if (searchQuery.trim()) {
        const search = searchQuery.toLowerCase().trim();
        fetchedItems = fetchedItems.filter(item => 
          item.title?.toLowerCase().includes(search) ||
          item.description?.toLowerCase().includes(search) ||
          item.sellerName?.toLowerCase().includes(search)
        );
      }
      
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error fetching marketplace items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseDetail = () => {
    setSelectedItem(null);
  };

  const handleFormSuccess = () => {
    setShowAddForm(false);
    fetchItems();
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `€${Number(price).toLocaleString()}`;
  };

  const getListingBadge = (item) => {
    if (item.listingType === 'both') return { class: 'badge-both', text: 'Sale & Rent' };
    if (item.listingType === 'rent') return { class: 'badge-rent', text: 'For Rent' };
    return { class: 'badge-sale', text: 'For Sale' };
  };

  const getSellerInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="marketplace">
        <div className="marketplace-loading">
          <div className="loading-spinner"></div>
          <p>Loading marketplace...</p>
        </div>
      </div>
    );
  }

  // Check admin access
  if (!isAdmin) {
    return (
      <div className="marketplace">
        <div className="marketplace-header">
          <h1>🛒 Dance Marketplace</h1>
          <p className="marketplace-subtitle">Coming soon! This feature is currently in development.</p>
        </div>
        <div className="marketplace-empty">
          <div className="empty-icon">🔒</div>
          <h3>Admin Access Only</h3>
          <p>The marketplace is currently available only to administrators during the beta phase.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace">
      {/* Header */}
      <div className="marketplace-header">
        <h1>🛒 Dance Marketplace</h1>
        <p className="marketplace-subtitle">
          Buy, sell, and rent dance costumes, shoes, and accessories
        </p>
        {isAdmin && (
          <div className="marketplace-admin-badge">
            <span>⚡</span>
            <span>Admin Mode</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="marketplace-actions">
        {canListItems && (
          <button 
            className="marketplace-btn marketplace-btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <span>➕</span>
            <span>List an Item</span>
          </button>
        )}
        
        {user && (
          <button 
            className={`marketplace-btn ${myListingsView ? 'marketplace-btn-primary' : 'marketplace-btn-secondary'}`}
            onClick={() => setMyListingsView(!myListingsView)}
          >
            <span>📋</span>
            <span>{myListingsView ? 'Browse All' : 'My Listings'}</span>
          </button>
        )}
        
        {isAdmin && (
          <Link to="/admin/marketplace" className="marketplace-btn marketplace-btn-admin">
            <span>🛡️</span>
            <span>Review Listings</span>
          </Link>
        )}
      </div>

      {/* Verification Notice */}
      {!canListItems && user && (
        <div className="marketplace-alert info">
          <span className="alert-icon">ℹ️</span>
          <div className="alert-content">
            <h4>Want to sell or rent items?</h4>
            <p>Only verified dancers can list items. Claim your dancer profile to get started!</p>
          </div>
        </div>
      )}

      {/* My Listings View */}
      {myListingsView ? (
        <>
          <div className="my-listings-header">
            <h2>My Listings</h2>
            <div className="my-listings-tabs">
              {[
                { id: 'all', label: 'All' },
                { id: 'pending', label: 'Pending' },
                { id: 'approved', label: 'Approved' },
                { id: 'rejected', label: 'Rejected' },
                { id: 'sold', label: 'Sold' },
                { id: 'rented', label: 'Rented' }
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`my-listing-tab ${myListingsFilter === tab.id ? 'active' : ''}`}
                  onClick={() => setMyListingsFilter(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Filters */}
          <div className="marketplace-filters">
            <div className="filter-group search-input">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search items, sellers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <label>Type</label>
              <select 
                value={listingTypeFilter} 
                onChange={(e) => setListingTypeFilter(e.target.value)}
              >
                {LISTING_TYPES.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="category-tabs">
            {CATEGORIES.map(category => (
              <button
                key={category.id}
                className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span>{category.icon}</span>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Items Grid */}
      {loading ? (
        <div className="marketplace-loading">
          <div className="loading-spinner"></div>
          <p>Loading items...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="marketplace-empty">
          <div className="empty-icon">
            {myListingsView ? '📋' : '🛒'}
          </div>
          <h3>{myListingsView ? 'No listings yet' : 'No items found'}</h3>
          <p>
            {myListingsView 
              ? 'Start selling or renting your dance items!'
              : 'Try adjusting your filters or check back later.'}
          </p>
          {myListingsView && canListItems && (
            <button 
              className="marketplace-btn marketplace-btn-primary"
              onClick={() => setShowAddForm(true)}
              style={{ marginTop: '1rem' }}
            >
              <span>➕</span>
              <span>List Your First Item</span>
            </button>
          )}
        </div>
      ) : (
        <div className="marketplace-grid">
          {items.map(item => (
            <div 
              key={item.id} 
              className="marketplace-item-card"
              onClick={() => handleItemClick(item)}
            >
              <div className="item-image-container">
                {item.images && item.images[0] ? (
                  <img 
                    src={item.images[0]} 
                    alt={item.title}
                    className="item-image"
                  />
                ) : (
                  <div className="item-image" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem'
                  }}>
                    {CATEGORIES.find(c => c.id === item.category)?.icon || '📦'}
                  </div>
                )}
                
                <div className="item-badges">
                  <span className={`item-badge ${getListingBadge(item).class}`}>
                    {getListingBadge(item).text}
                  </span>
                  {item.isVerifiedSeller && (
                    <span className="item-badge badge-verified">✓ Verified</span>
                  )}
                </div>

                {myListingsView && (
                  <span className={`listing-status-badge ${item.status}`}>
                    {item.status}
                  </span>
                )}
              </div>

              <div className="item-content">
                <div className="item-category">
                  {CATEGORIES.find(c => c.id === item.category)?.label || item.category}
                </div>
                
                <h3 className="item-title">{item.title}</h3>
                
                <p className="item-description">{item.description}</p>
                
                <div className="item-meta">
                  <div className="item-price">
                    {(item.listingType === 'sale' || item.listingType === 'both') && (
                      <>
                        <span className="price-label">Price</span>
                        <span className="price-value">{formatPrice(item.salePrice)}</span>
                      </>
                    )}
                    {(item.listingType === 'rent' || item.listingType === 'both') && (
                      <span className="price-rent">
                        {formatPrice(item.rentPricePerDay)}/day
                      </span>
                    )}
                  </div>
                  
                  <div className="item-seller">
                    <div className="seller-avatar">
                      {getSellerInitials(item.sellerName)}
                    </div>
                    <span>{item.sellerName}</span>
                  </div>
                </div>

                <div className="item-footer">
                  <button className="item-action-btn view">
                    <span>👁️</span>
                    <span>View</span>
                  </button>
                  <button className="item-action-btn contact">
                    <span>💬</span>
                    <span>Contact</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Modal */}
      {showAddForm && (
        <div className="marketplace-modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="marketplace-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>List an Item</h2>
              <button className="modal-close" onClick={() => setShowAddForm(false)}>×</button>
            </div>
            <div className="modal-content">
              <MarketplaceItemForm 
                onSuccess={handleFormSuccess}
                onCancel={() => setShowAddForm(false)}
                sellerName={isAdmin ? 'Admin' : verifiedDancerName}
                isAdmin={isAdmin}
              />
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="marketplace-modal-overlay" onClick={handleCloseDetail}>
          <div className="marketplace-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
            <div className="modal-header">
              <h2>{selectedItem.title}</h2>
              <button className="modal-close" onClick={handleCloseDetail}>×</button>
            </div>
            <div className="modal-content">
              <MarketplaceItemDetail 
                item={selectedItem}
                onClose={handleCloseDetail}
                isOwner={user && selectedItem.sellerId === user.uid}
                isAdmin={isAdmin}
                onUpdate={fetchItems}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
